import { useEffect } from "react";

import { site } from "../lib/site.js";

/**
 * Sets a meta tag, creating it if the shell did not ship one.
 *
 * The create path is not defensive padding. In production the Node server
 * appends these tags to the shell, so they exist to be updated — but the Vite
 * dev server serves `index.html` untouched, and that file carries no
 * description. Without this the hook silently did nothing there: no error, no
 * tag, and no way to notice until something scraped a dev URL.
 */
function setMeta(name: string, kind: "name" | "property", content: string): void {
  const selector = `meta[${kind}="${name}"]`;
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(kind, name);
    document.head.append(element);
  }

  element.setAttribute("content", content);
}

/**
 * Keeps the document title and description in step during client-side
 * navigation.
 *
 * The Node server already injects the correct tags into the shell on the
 * initial request — that is what crawlers and link scrapers read. This only
 * covers in-app navigation, where no new document is fetched and the injected
 * head would otherwise be stale in the tab title and browser history.
 */
export function usePageMeta(title: string | null, description?: string): void {
  useEffect(() => {
    if (title === null) return;

    const fullTitle =
      title === site.brand ? title : `${title} · ${site.brand}`;
    const previous = document.title;
    document.title = fullTitle;

    if (description) {
      setMeta("description", "name", description);
      setMeta("og:description", "property", description);
    }

    setMeta("og:title", "property", fullTitle);

    return () => {
      document.title = previous;
    };
  }, [title, description]);
}
